(() => {
  "use strict";

  const CONFIG = {
    /*
     * The variant this module adds: the trial-enabled
     * Chapie VIP membership.
     */
    vipVariantId: 55801084706984,
    vipSellingPlanId: 2899050664,
    vipProductHandle: "vip-membership-1",
    trialDays: 3,

    /*
     * Every membership variant across all tiers. Used
     * ONLY to detect a membership already in the cart so
     * a second one is never offered or added — one
     * membership per cart. Lines matched here are never
     * modified or removed; this module only manages the
     * lines it created itself (see isOurOfferLine).
     */
    membershipVariantIds: [
      55801084706984, // Chapie VIP (trial, plan 2899050664)
      55216631742632 // Chapie VIP GOLD (plan 2640740520)
    ],

    subscriptionPolicyUrl:
      "https://www.mychapie.com/policies/subscription-policy",

    /*
     * Product art for the interrupt popup's wide layout
     * (decorative, hidden below 1100px).
     */
    brandAssets: {
      containerWhite:
        "https://cdn.shopify.com/s/files/1/0470/8263/5432/files/" +
        "2_47e75956-ac35-4e57-ad74-4de90de73951.webp?v=1786472031",

      containerBlue:
        "https://cdn.shopify.com/s/files/1/0470/8263/5432/files/" +
        "3_5f0684d3-8ab5-409f-bb3f-3cee8ec09934.webp?v=1786472032"
    },

    /*
     * Displayed savings line: "Save $6.83 on this order".
     * Matches the waived standard shipping value in
     * policy §9. Confirmed with the merchant that no cart
     * ships free without a membership, so this is always
     * a charge the customer actually avoids.
     *
     * If a free-shipping threshold, promo, or free-ship
     * product is ever introduced, this claim becomes
     * false for those carts and must be gated rather
     * than shown unconditionally. Keep this value, the
     * policy §9 figure, and the real shipping rate in
     * sync.
     */
    standardShippingCents: 683,

    sourceProperty: "_chapie_vip_source",
    sourceValue: "automatic_cart_offer",

    /*
     * Consent record written onto every VIP line. The
     * underscore prefix keeps these hidden from the
     * customer-facing cart while preserving them on the
     * order. Bump offerVersion whenever price, trial
     * length, or terms wording changes so consent is
     * always attributable to the exact offer shown.
     */
    offerVersion: "2026-08-12-v1",
    offerVersionProperty: "_chapie_vip_offer_version",
    consentAtProperty: "_chapie_vip_consent_at",
    consentSurfaceProperty: "_chapie_vip_consent_surface",
    consentTermsProperty: "_chapie_vip_consent_terms",

    /*
     * How long to watch for a late-injected cart
     * container before giving up. Bounded so the
     * fallback observer is never permanent.
     */
    containerWatchMs: 20000,

    pollingDurationMs: 4000,
    pollingIntervalMs: 175,
    reconcileDelayMs: 200,
    observerCooldownMs: 300,
    postRefreshDelayMs: 450,
    maxRemoveAttempts: 5,

    debug: false
  };

  const COPY = {
    badge: "One-Time Special Offer",
    headlineTop: "Get FREE",
    headlineBottom: "U.S. Shipping",
    trialLine: "Try Chapie VIP free for 3 days",
    ctaLabel: "GET FREE VIP SHIPPING",
    declineLabel: "No thanks, I'll pay for shipping",

    /*
     * Marketing copy, not disclosure. The tiles keep the
     * "U.S." qualifier because free shipping excludes
     * expedited, international, oversized, and wholesale
     * orders; the Terms link carries the full
     * qualifications.
     */
    benefits: [
      "Unlimited FREE Shipping",
      "VIP Exclusive Drops",
      "20–60% OFF VIP Deals"
    ],

    termsLinkLabel: "Terms",
    close: "Close"
  };

  const STORAGE = {
    interruptSeen: "chapie_vip:interrupt_seen",
    removedByUser: "chapie_vip:removed_by_user"
  };

  const STATE = {
    addPromise: null,
    removePromise: null,
    reconcilePromise: null,

    reconcileQueued: false,
    ignoreObserverUntil: 0,

    productPromise: null,
    productData: null,

    termsPromise: null,
    termsData: null,

    toggleBusy: false,
    uiRevision: 0,

    modalElement: null,
    modalOpen: false,
    modalBusy: false,
    pendingCheckoutTrigger: null,
    bypassInterrupt: false,
    interceptInFlight: false,
    interruptSeenMemory: false,
    removedByUserMemory: false,
    scrollY: 0,

    modalPointerDown: null,
    modalActionAt: 0,
    modalOpener: null,
    modalHideTimer: null,

    pollingTimer: null,
    pollingStartedAt: 0,

    reconcileTimer: null,
    observerRetryTimer: null,
    postRefreshTimer: null,

    observers: [],
    containerWatcher: null,
    containerWatchTimer: null
  };

  function log(...args) {
    if (CONFIG.debug) {
      console.log("[Chapie VIP]", ...args);
    }
  }

  function warn(...args) {
    console.warn("[Chapie VIP]", ...args);
  }

  function rootUrl(path) {
    const root = window.Shopify?.routes?.root || "/";
    return `${root}${path}`.replace(/\/+/g, "/");
  }

  async function jsonRequest(path, options = {}) {
    const response = await fetch(rootUrl(path), {
      credentials: "same-origin",
      ...options,
      headers: {
        Accept: "application/json",
        ...(options.body
          ? { "Content-Type": "application/json" }
          : {}),
        ...(options.headers || {})
      }
    });

    if (!response.ok) {
      const body = await response.text();
      throw new Error(
        body || `Request failed with status ${response.status}`
      );
    }

    return response.json();
  }

  function getCart() {
    return jsonRequest("cart.js");
  }

  async function getVipProduct() {
    if (STATE.productData) {
      return STATE.productData;
    }

    if (STATE.productPromise) {
      return STATE.productPromise;
    }

    STATE.productPromise = jsonRequest(
      `products/${CONFIG.vipProductHandle}.js`
    );

    try {
      STATE.productData = await STATE.productPromise;
      return STATE.productData;
    } finally {
      STATE.productPromise = null;
    }
  }

  function isVipVariant(item) {
    return (
      Number(item?.variant_id) === Number(CONFIG.vipVariantId)
    );
  }

  /*
   * Any membership tier, not just the one this module
   * adds. Gold and the trial plan are different variants
   * of different products.
   */
  function isMembershipVariant(item) {
    const id = Number(item?.variant_id);

    return CONFIG.membershipVariantIds.some(
      candidate => Number(candidate) === id
    );
  }

  /*
   * Division of labor: Appstle enforces plan/variant
   * eligibility via customer tags at checkout. This
   * script manages exclusively the lines it added
   * itself — identified by its own marker property.
   * Every VIP line a customer adds manually (trial
   * plan, Gold, anything) is invisible to this script:
   * never removed, never toggled. Eligibility for those
   * is Appstle's job.
   */
  function isOurOfferLine(item) {
    return (
      isVipVariant(item) &&
      item?.properties?.[CONFIG.sourceProperty] ===
        CONFIG.sourceValue
    );
  }

  function hasAnyVipLine(cart) {
    return Boolean(cart?.items?.some(isMembershipVariant));
  }

  function isQualifyingProduct(item) {
    return (
      !isMembershipVariant(item) &&
      item?.gift_card !== true &&
      item?.requires_shipping !== false
    );
  }

  function hasQualifyingProduct(cart) {
    return Boolean(cart?.items?.some(isQualifyingProduct));
  }

  function findVipLine(cart) {
    return cart?.items?.find(isOurOfferLine);
  }

  function findVipLines(cart) {
    return cart?.items?.filter(isOurOfferLine) || [];
  }

  function storageGet(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      log("Session storage read failed:", error);
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      log("Session storage write failed:", error);
    }
  }

  /*
   * The in-memory flag backs up sessionStorage. If
   * storage writes throw (e.g. Safari private mode),
   * the popup would otherwise reopen on every checkout
   * press after a dismissal — an indefinite gate. The
   * memory flag guarantees once-per-pageview even with
   * no storage; sessionStorage extends that to
   * once-per-session when available.
   */
  function interruptSeen() {
    return (
      STATE.interruptSeenMemory ||
      storageGet(STORAGE.interruptSeen) === "true"
    );
  }

  function setInterruptSeen() {
    STATE.interruptSeenMemory = true;
    storageSet(STORAGE.interruptSeen, "true");
  }

  /*
   * Set when the customer takes VIP back out of the
   * cart from the widget. It selects the compact
   * "removed" bar over the full offer card — they have
   * already seen the full pitch, so a quiet one-tap way
   * back is the right follow-up. Memory-backed for the
   * same reason as interruptSeen.
   */
  function removedByUser() {
    return (
      STATE.removedByUserMemory ||
      storageGet(STORAGE.removedByUser) === "true"
    );
  }

  function setRemovedByUser() {
    STATE.removedByUserMemory = true;
    storageSet(STORAGE.removedByUser, "true");
  }

  function customerFlagsLoaded() {
    return window.ChapieVipCustomer?.loaded === true;
  }

  function isActiveVipMember() {
    return window.ChapieVipCustomer?.activeMember === true;
  }

  function isTrialUsed() {
    return window.ChapieVipCustomer?.trialUsed === true;
  }

  /*
   * Fails closed. If the customer flags snippet is
   * missing or did not render, the offer is treated as
   * blocked rather than shown to an unknown customer.
   *
   * Blocked for active members of ANY membership tier
   * and for any customer tagged vip-trial-used.
   *
   * KNOWN GAP — the one-time trial is NOT enforced for
   * logged-out returning customers.
   *
   * Customer tags are only readable when Shopify has a
   * customer object, so a logged-out prior trial user
   * reads as eligible here and is shown the offer.
   * Appstle does not catch this either: confirmed with
   * the merchant that it cannot identify the customer
   * at checkout, so it will not reject a repeat trial.
   *
   * There is therefore NO enforcement of the one-time
   * limit for guests anywhere in the current stack.
   * Closing it requires action outside this file — a
   * post-order Shopify Flow that checks the resolved
   * customer's tags after checkout, and/or requiring an
   * account to claim the trial. Do not assume this
   * function provides the guarantee.
   */
  function isOfferBlocked() {
    if (!customerFlagsLoaded()) {
      return true;
    }

    return isActiveVipMember() || isTrialUsed();
  }

  function getOfferInstances() {
    return Array.from(
      document.querySelectorAll("[data-chapie-vip-offer]")
    );
  }

  function getInstanceElements(container) {
    return {
      active: container.querySelector(
        "[data-chapie-vip-active]"
      ),

      inactive: container.querySelector(
        "[data-chapie-vip-inactive]"
      ),

      cta: container.querySelector(
        "[data-chapie-vip-cta]"
      ),

      removed: container.querySelector(
        "[data-chapie-vip-removed]"
      ),

      removeCheck: container.querySelector(
        "[data-chapie-vip-remove]"
      ),

      readdCheck: container.querySelector(
        "[data-chapie-vip-readd]"
      ),

      removedCopy: container.querySelector(
        "[data-chapie-vip-removed-copy]"
      ),

      removedTerms: container.querySelector(
        "[data-chapie-vip-removed-terms]"
      ),

      save: container.querySelector(
        "[data-chapie-vip-save]"
      ),

      footerTerms: container.querySelector(
        "[data-chapie-vip-footer-terms]"
      ),

      activeCopy: container.querySelector(
        "[data-chapie-vip-active-copy]"
      ),

      status: container.querySelector(
        "[data-chapie-vip-status]"
      )
    };
  }

  function setStatus(message = "") {
    getOfferInstances().forEach(container => {
      const { status } = getInstanceElements(container);

      if (status) {
        status.textContent = message;
      }
    });
  }

  function setControlsDisabled(disabled) {
    getOfferInstances().forEach(container => {
      const elements = getInstanceElements(container);

      if (elements.cta) {
        elements.cta.disabled = disabled;
      }

      if (elements.removeCheck) {
        elements.removeCheck.disabled = disabled;
      }

      if (elements.readdCheck) {
        elements.readdCheck.disabled = disabled;
      }
    });
  }

  function formatWithDelimiters(
    number,
    precision = 2,
    thousands = ",",
    decimal = "."
  ) {
    const numeric = Number(number);

    if (!Number.isFinite(numeric)) {
      return "0";
    }

    const fixed = (numeric / 100).toFixed(precision);
    const parts = fixed.split(".");

    const whole = parts[0].replace(
      /(\d)(?=(\d{3})+(?!\d))/g,
      `$1${thousands}`
    );

    const fraction =
      precision > 0 && parts[1]
        ? `${decimal}${parts[1]}`
        : "";

    return `${whole}${fraction}`;
  }

  function stripHtml(value) {
    return String(value || "")
      .replace(/<[^>]*>/g, "")
      .trim();
  }

  function formatMoneyWithTemplate(cents, template) {
    if (!template || !template.includes("{{")) {
      return null;
    }

    const replacements = {
      amount: formatWithDelimiters(cents, 2, ",", "."),

      amount_no_decimals: formatWithDelimiters(
        cents,
        0,
        ",",
        "."
      ),

      amount_with_comma_separator: formatWithDelimiters(
        cents,
        2,
        ".",
        ","
      ),

      amount_no_decimals_with_comma_separator:
        formatWithDelimiters(cents, 0, ".", ","),

      amount_with_apostrophe_separator:
        formatWithDelimiters(cents, 2, "'", ".")
    };

    const result = template.replace(
      /\{\{\s*(\w+)\s*\}\}/g,
      (match, key) =>
        Object.prototype.hasOwnProperty.call(
          replacements,
          key
        )
          ? replacements[key]
          : match
    );

    return stripHtml(result);
  }

  function formatMoney(cents, currencyCode) {
    const offer = getOfferInstances()[0];
    const moneyFormat = offer?.dataset?.moneyFormat || "";

    if (typeof window.Shopify?.formatMoney === "function") {
      try {
        const formatted = window.Shopify.formatMoney(
          cents,
          moneyFormat || undefined
        );

        const clean = stripHtml(formatted);

        if (clean && !clean.includes("{{")) {
          return clean;
        }
      } catch (error) {
        log("Shopify money formatting failed:", error);
      }
    }

    const templateResult = formatMoneyWithTemplate(
      cents,
      moneyFormat
    );

    if (templateResult) {
      return templateResult;
    }

    try {
      return new Intl.NumberFormat(
        document.documentElement.lang || "en-US",
        {
          style: "currency",
          currency: currencyCode || "USD"
        }
      ).format(Number(cents || 0) / 100);
    } catch {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  }

  function findVipVariant(product) {
    return product?.variants?.find(
      variant =>
        Number(variant.id) === Number(CONFIG.vipVariantId)
    );
  }

  function findProductSellingPlan(product) {
    for (const group of product?.selling_plan_groups || []) {
      const plan = group.selling_plans?.find(
        candidate =>
          Number(candidate.id) ===
          Number(CONFIG.vipSellingPlanId)
      );

      if (plan) {
        return plan;
      }
    }

    return null;
  }

  function applyPriceAdjustment(basePrice, adjustment) {
    if (!adjustment) {
      return basePrice;
    }

    const value = Number(adjustment.value || 0);

    switch (adjustment.value_type) {
      case "percentage":
        return Math.round(basePrice * ((100 - value) / 100));

      case "fixed_amount":
        return Math.max(0, Math.round(basePrice - value));

      case "price":
        return Math.round(value);

      default:
        return basePrice;
    }
  }

  function getRecurringPriceCents({ variant, productPlan }) {
    const basePrice = Number(variant?.price ?? 0);

    const adjustments = productPlan?.price_adjustments || [];

    /*
     * Appstle implements the free trial as a price
     * adjustment, and can report the 100%-off trial
     * WITHOUT an order_count. Applying it as the ongoing
     * price yields $0.00 — the trial cycle, not the
     * recurring price. A zero result is therefore
     * rejected and the base variant price used instead.
     *
     * This keeps the displayed price parametric: change
     * the VIP variant price in Shopify and every surface
     * follows automatically.
     */
    const ongoingAdjustment = adjustments.find(
      adjustment => adjustment?.order_count == null
    );

    if (ongoingAdjustment) {
      const adjusted = applyPriceAdjustment(
        basePrice,
        ongoingAdjustment
      );

      if (adjusted > 0) {
        return adjusted;
      }
    }

    return basePrice;
  }

  function getBillingInterval(productPlan) {
    const text = [
      productPlan?.name,

      ...(productPlan?.options || []).map(
        option => option?.value
      )
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    if (text.includes("year")) {
      return "year";
    }

    if (text.includes("week")) {
      return "week";
    }

    if (text.includes("day")) {
      return "day";
    }

    return "month";
  }

  /*
   * Single source of truth for the subscription terms.
   * The widget's added state, the popup, and the
   * recorded consent property all derive from this one
   * string, so a customer can never be shown one set of
   * terms and have another recorded.
   */
  function buildTermsText(terms) {
    return (
      `${CONFIG.trialDays}-day free trial, then ` +
      `${terms.recurringPrice}/${terms.billingInterval}. ` +
      `Renews automatically until canceled. ` +
      `Cancel anytime in your account.`
    );
  }

  /*
   * Resolves the recurring price and interval from the
   * product JSON. Throws if no confirmed non-zero price
   * is available — callers then suppress the offer
   * entirely rather than display incomplete terms.
   */
  async function getOfferTerms() {
    if (STATE.termsData) {
      return STATE.termsData;
    }

    if (STATE.termsPromise) {
      return STATE.termsPromise;
    }

    STATE.termsPromise = (async () => {
      const product = await getVipProduct();
      const variant = findVipVariant(product);
      const productPlan = findProductSellingPlan(product);

      if (!variant) {
        throw new Error(
          "VIP variant not found in product JSON."
        );
      }

      if (!productPlan) {
        throw new Error(
          "VIP selling plan not found in product JSON."
        );
      }

      const cents = getRecurringPriceCents({
        variant,
        productPlan
      });

      if (!(Number(cents) > 0)) {
        throw new Error(
          "No confirmed recurring price available."
        );
      }

      const terms = {
        product,
        variant,
        recurringPriceCents: Number(cents),
        recurringPrice: formatMoney(cents, null),
        billingInterval: getBillingInterval(productPlan),
        title: product?.title || "Chapie VIP"
      };

      terms.text = buildTermsText(terms);

      STATE.termsData = terms;

      return terms;
    })();

    try {
      return await STATE.termsPromise;
    } finally {
      STATE.termsPromise = null;
    }
  }

  /*
   * Widget footer / popup footer variant. Derived from
   * the same terms object as buildTermsText; the trial
   * length it omits is disclosed in the card body
   * directly above it.
   */
  function buildShortTermsText(terms) {
    return (
      `Then ${terms.recurringPrice}/` +
      `${terms.billingInterval}. Auto-renews until ` +
      `canceled. Cancel anytime.`
    );
  }

  const ICONS = {
    star:
      '<svg viewBox="0 0 24 24" aria-hidden="true" ' +
      'focusable="false"><path d="M12 2.6l2.8 5.9 6.4.8' +
      '-4.7 4.4 1.2 6.3L12 17l-5.7 3 1.2-6.3L2.8 9.3l6.4' +
      '-.8z" fill="currentColor"/></svg>',

    truck:
      '<svg viewBox="0 0 24 24" aria-hidden="true" ' +
      'focusable="false"><path d="M3 7h10v8H3zM13 10h4l3 ' +
      '3v2h-7z" fill="none" stroke="currentColor" ' +
      'stroke-width="1.7" stroke-linejoin="round"/>' +
      '<circle cx="7" cy="17" r="1.8" fill="none" ' +
      'stroke="currentColor" stroke-width="1.7"/>' +
      '<circle cx="16.5" cy="17" r="1.8" fill="none" ' +
      'stroke="currentColor" stroke-width="1.7"/></svg>',

    starOutline:
      '<svg viewBox="0 0 24 24" aria-hidden="true" ' +
      'focusable="false"><path d="M12 3.4l2.5 5.2 5.7.7' +
      '-4.2 3.9 1.1 5.6L12 16.1l-5.1 2.7 1.1-5.6-4.2-3.9 ' +
      '5.7-.7z" fill="none" stroke="currentColor" ' +
      'stroke-width="1.7" stroke-linejoin="round"/></svg>',

    tag:
      '<svg viewBox="0 0 24 24" aria-hidden="true" ' +
      'focusable="false"><path d="M4 4h7l9 9-7 7-9-9z" ' +
      'fill="none" stroke="currentColor" ' +
      'stroke-width="1.7" stroke-linejoin="round"/>' +
      '<circle cx="8.5" cy="8.5" r="1.4" ' +
      'fill="currentColor"/></svg>'
  };

  function buildTermsLink() {
    const link = document.createElement("a");

    link.href = CONFIG.subscriptionPolicyUrl;
    link.target = "_blank";
    link.rel = "noopener";
    link.textContent = COPY.termsLinkLabel;
    link.className = "chapie-vip-offer__terms-link";

    return link;
  }

  /*
   * Builds the offer card interior — badge, headline,
   * savings, trial line, CTA, benefit tiles, footer
   * terms — from the same terms object everywhere it
   * appears. Used by the popup; the cart widget renders
   * the identical structure from Liquid with the same
   * classes, so the two surfaces stay pixel-identical.
   */
  function buildOfferCardContent(
    terms,
    { ctaAttr, declineLink = false } = {}
  ) {
    const fragment = document.createDocumentFragment();

    const badge = document.createElement("span");

    badge.className = "chapie-vip-offer__badge";
    badge.innerHTML = ICONS.star;

    badge.querySelector("svg").classList.add(
      "chapie-vip-offer__badge-star"
    );

    badge.append(
      document.createTextNode(` ${COPY.badge}`)
    );

    fragment.append(badge);

    const headline = document.createElement("h3");

    headline.className = "chapie-vip-offer__headline";
    headline.id = "chapie-vip-modal-title";

    /*
     * Trailing space matters: phones hide the <br> and
     * the two text nodes join, so without it the line
     * renders as "Get FREEU.S. Shipping".
     */
    headline.append(
      document.createTextNode(`${COPY.headlineTop} `),
      document.createElement("br"),
      document.createTextNode(COPY.headlineBottom)
    );

    fragment.append(headline);

    const save = document.createElement("p");

    save.className = "chapie-vip-offer__save";

    save.textContent =
      `Save ${formatMoney(
        CONFIG.standardShippingCents,
        null
      )} on this order`;

    fragment.append(save);

    const trial = document.createElement("p");

    trial.className = "chapie-vip-offer__trial";
    trial.textContent = COPY.trialLine;

    fragment.append(trial);

    const cta = document.createElement("button");

    cta.type = "button";
    cta.className = "chapie-vip-offer__cta";

    if (ctaAttr) {
      cta.setAttribute(ctaAttr, "");
    }

    const ctaLabel = document.createElement("span");

    ctaLabel.className = "chapie-vip-offer__cta-label";
    ctaLabel.textContent = COPY.ctaLabel;

    cta.append(ctaLabel);

    fragment.append(cta);

    const benefits = document.createElement("ul");

    benefits.className = "chapie-vip-offer__benefits";

    const icons = [
      ICONS.truck,
      ICONS.starOutline,
      ICONS.tag
    ];

    COPY.benefits.forEach((label, index) => {
      const item = document.createElement("li");

      item.className = "chapie-vip-offer__benefit";
      item.innerHTML = icons[index];

      item.querySelector("svg").classList.add(
        "chapie-vip-offer__benefit-icon"
      );

      const text = document.createElement("span");

      text.textContent = label;

      item.append(text);

      benefits.append(item);
    });

    fragment.append(benefits);

    /*
     * Popup-only decline affordance, sitting between the
     * benefit tiles and the disclosure: declines the
     * offer and continues to the checkout the customer
     * asked for. An explicit, honestly-labelled "no"
     * whose behavior matches its wording.
     */
    if (declineLink) {
      const decline = document.createElement("button");

      decline.type = "button";
      decline.className = "chapie-vip-offer__decline-link";
      decline.setAttribute("data-chapie-vip-decline", "");
      decline.textContent = COPY.declineLabel;

      fragment.append(decline);
    }

    const footer = document.createElement("p");

    footer.className = "chapie-vip-offer__footer-terms";

    footer.append(
      document.createTextNode(
        `${buildShortTermsText(terms)} `
      )
    );

    footer.append(buildTermsLink());

    fragment.append(footer);

    return fragment;
  }

  /*
   * Fills both card states. Note the added state shows
   * the amount only — no terms. Disclosure happens
   * before consent (popup or widget card) and the exact
   * string shown is recorded on the line; restating it
   * after the fact is redundant, and Shopify's checkout
   * presents the subscription terms again regardless.
   */
  function applyOfferContent(terms) {
    const shortTerms = buildShortTermsText(terms);

    const savings = formatMoney(
      CONFIG.standardShippingCents,
      null
    );

    getOfferInstances().forEach(container => {
      const elements = getInstanceElements(container);

      if (elements.save) {
        elements.save.textContent =
          `Save ${savings} on this order`;
      }

      if (elements.footerTerms) {
        elements.footerTerms.textContent = "";

        elements.footerTerms.append(
          document.createTextNode(`${shortTerms} `)
        );

        elements.footerTerms.append(buildTermsLink());
      }

      /*
       * "$0.00 today" is the trial's first cycle. If the
       * trial is ever removed or made paid, this line
       * and CONFIG.trialDays both stop being true.
       */
      if (elements.removedCopy) {
        elements.removedCopy.textContent =
          `Try Chapie VIP to save ${savings} on ` +
          `shipping — ${formatMoney(0, null)} today`;
      }

      /*
       * The removed state's check button re-enrolls, so
       * the recurring terms are disclosed beside it.
       */
      if (elements.removedTerms) {
        elements.removedTerms.textContent = "";

        elements.removedTerms.append(
          document.createTextNode(`${shortTerms} `)
        );

        elements.removedTerms.append(buildTermsLink());
      }

      if (elements.activeCopy) {
        elements.activeCopy.textContent =
          `You're saving ${savings} in shipping with ` +
          `Chapie VIP`;
      }
    });
  }

  /*
   * Three mutually exclusive states:
   *   "offer"   full gradient card with the big CTA
   *   "added"   compact confirmation, check removes
   *   "removed" grey bar, check re-enrolls
   */
  function setOfferVisibility({ visible, state = null }) {
    getOfferInstances().forEach(container => {
      const elements = getInstanceElements(container);

      container.hidden = !visible;

      if (elements.inactive) {
        elements.inactive.hidden =
          !visible || state !== "offer";
      }

      if (elements.active) {
        elements.active.hidden =
          !visible || state !== "added";
      }

      if (elements.removed) {
        elements.removed.hidden =
          !visible || state !== "removed";
      }

      if (elements.cta) {
        elements.cta.disabled = STATE.toggleBusy;
      }

      if (elements.removeCheck) {
        elements.removeCheck.disabled = STATE.toggleBusy;
      }

      if (elements.readdCheck) {
        elements.readdCheck.disabled = STATE.toggleBusy;
      }

      container.dataset.vipInCart =
        state === "added" ? "true" : "false";
    });
  }

  async function refreshOfferUi(suppliedCart = null) {
    const instances = getOfferInstances();

    if (!instances.length) {
      return;
    }

    const revision = ++STATE.uiRevision;

    const cart = suppliedCart || (await getCart());

    /*
     * Bail before touching visibility if a newer refresh
     * started while this one awaited. Without this, a
     * slow early-return branch could hide the widget
     * over the top of fresher state.
     */
    if (revision !== STATE.uiRevision) {
      return;
    }

    const vipLine = findVipLine(cart);
    const hasVip = Boolean(vipLine);

    /*
     * Suppress entirely when: blocked customer, empty of
     * qualifying merchandise, or a membership line this
     * module does not own is present (never upsell a
     * second membership on top of one being bought).
     */
    if (
      isOfferBlocked() ||
      !hasQualifyingProduct(cart) ||
      (hasAnyVipLine(cart) && !hasVip)
    ) {
      setOfferVisibility({ visible: false });
      return;
    }

    /*
     * State selection:
     *   in cart            -> "added"
     *   taken back out     -> "removed" (compact re-add)
     *   popup dismissed    -> "offer" (full card)
     *   otherwise          -> hidden, the popup owns it
     *
     * The removed bar wins over the full offer card
     * because that customer has already seen the full
     * pitch; repeating it would be nagging rather than
     * offering.
     */
    let state = null;

    if (hasVip) {
      state = "added";
    } else if (removedByUser()) {
      state = "removed";
    } else if (interruptSeen()) {
      state = "offer";
    }

    if (!state) {
      setOfferVisibility({ visible: false });
      return;
    }

    let terms;

    try {
      terms = await getOfferTerms();
    } catch (error) {
      /*
       * No confirmed terms means no offer, in either
       * state. A recurring charge is never presented
       * without its price and renewal terms.
       */
      warn("Terms unavailable — offer suppressed.", error);

      setOfferVisibility({ visible: false });

      return;
    }

    if (revision !== STATE.uiRevision) {
      return;
    }

    applyOfferContent(terms);

    setOfferVisibility({ visible: true, state });
  }

  function buildConsentProperties(terms, surface) {
    return {
      [CONFIG.sourceProperty]: CONFIG.sourceValue,
      [CONFIG.offerVersionProperty]: CONFIG.offerVersion,
      [CONFIG.consentAtProperty]: new Date().toISOString(),
      [CONFIG.consentSurfaceProperty]: surface,
      [CONFIG.consentTermsProperty]: terms.text
    };
  }

  async function addVipLine(terms, surface) {
    return jsonRequest("cart/add.js", {
      method: "POST",
      body: JSON.stringify({
        items: [
          {
            id: Number(CONFIG.vipVariantId),
            quantity: 1,
            selling_plan: Number(CONFIG.vipSellingPlanId),
            properties: buildConsentProperties(
              terms,
              surface
            )
          }
        ]
      })
    });
  }

  function isOnCartPage() {
    return (
      Boolean(
        document.querySelector(
          ".shopify-section--main-cart"
        )
      ) || /\/cart\/?$/.test(window.location.pathname)
    );
  }

  function dispatchCartRefresh(cart, { reload = true } = {}) {
    document.dispatchEvent(
      new CustomEvent("cart:refresh", {
        detail: {
          cart,
          source: "chapie-vip"
        }
      })
    );

    /*
     * The full cart page is server-rendered and does not
     * react to cart events, so reload it to reflect the
     * changed line items. Suppressed when the caller is
     * about to navigate to checkout anyway.
     */
    if (reload && isOnCartPage()) {
      window.location.reload();
      return;
    }

    window.clearTimeout(STATE.postRefreshTimer);

    STATE.postRefreshTimer = window.setTimeout(() => {
      bindOfferInstances();
      scheduleReconcile();
    }, CONFIG.postRefreshDelayMs);
  }

  /*
   * Adds VIP. Only ever called from an affirmative
   * customer action — the cart widget CTA or the popup
   * CTA. There is no automatic path into this function.
   */
  async function ensureVip({
    surface = "cart_widget",
    reload = true
  } = {}) {
    if (isOfferBlocked()) {
      return getCart();
    }

    if (STATE.addPromise) {
      return STATE.addPromise;
    }

    const currentPromise = (async () => {
      let cart = await getCart();

      if (!hasQualifyingProduct(cart)) {
        return cart;
      }

      if (findVipLine(cart)) {
        await refreshOfferUi(cart);
        return cart;
      }

      /*
       * A manual membership line (Gold, or a direct
       * purchase) blocks the add — one membership per
       * cart.
       */
      if (hasAnyVipLine(cart)) {
        await refreshOfferUi(cart);
        return cart;
      }

      const terms = await getOfferTerms();

      await addVipLine(terms, surface);

      cart = await getCart();

      await refreshOfferUi(cart);

      dispatchCartRefresh(cart, { reload });

      document.dispatchEvent(
        new CustomEvent("chapie:vip-added", {
          detail: { cart, surface }
        })
      );

      return cart;
    })();

    STATE.addPromise = currentPromise;

    try {
      return await currentPromise;
    } finally {
      if (STATE.addPromise === currentPromise) {
        STATE.addPromise = null;
      }
    }
  }

  async function removeVip() {
    if (STATE.removePromise) {
      return STATE.removePromise;
    }

    const currentPromise = (async () => {
      let cart = await getCart();

      for (
        let attempt = 0;
        attempt < CONFIG.maxRemoveAttempts;
        attempt += 1
      ) {
        const vipLines = findVipLines(cart);

        if (!vipLines.length) {
          break;
        }

        for (const vipLine of vipLines) {
          await jsonRequest("cart/change.js", {
            method: "POST",
            body: JSON.stringify({
              id: vipLine.key,
              quantity: 0
            })
          });
        }

        cart = await getCart();
      }

      if (findVipLine(cart)) {
        throw new Error(
          "Chapie VIP could not be removed after multiple attempts."
        );
      }

      await refreshOfferUi(cart);

      dispatchCartRefresh(cart);

      document.dispatchEvent(
        new CustomEvent("chapie:vip-removed", {
          detail: { cart }
        })
      );

      return cart;
    })();

    STATE.removePromise = currentPromise;

    try {
      return await currentPromise;
    } finally {
      if (STATE.removePromise === currentPromise) {
        STATE.removePromise = null;
      }
    }
  }

  /*
   * Reconcile never adds anything. VIP enters the cart
   * only through an affirmative customer action. The
   * only mutation here is cleanup: removing a line this
   * module added when it is no longer valid.
   */
  async function runReconcile() {
    if (STATE.removePromise) {
      await STATE.removePromise.catch(() => {});
    }

    let cart = await getCart();
    let hasVip = Boolean(findVipLine(cart));

    if (isOfferBlocked()) {
      /*
       * Clean up only a line THIS module added — e.g.
       * added as a guest, then logged in as a member.
       * Manual membership lines pass through untouched
       * for Appstle to handle.
       */
      if (hasVip) {
        cart = await removeVip();
      }

      await refreshOfferUi(cart);

      return cart;
    }

    /*
     * Remove an orphan VIP line when no physical
     * merchandise remains in the cart.
     */
    if (hasVip && !hasQualifyingProduct(cart)) {
      cart = await removeVip();
      hasVip = false;
    }

    /*
     * One membership per cart means one unit. If a
     * quantity stepper or an app ever multiplies our
     * line, normalise it rather than letting the
     * customer be billed for two memberships.
     */
    if (hasVip && !STATE.addPromise && !STATE.removePromise) {
      const ourLine = findVipLine(cart);

      if (ourLine && Number(ourLine.quantity) !== 1) {
        await jsonRequest("cart/change.js", {
          method: "POST",
          body: JSON.stringify({
            id: ourLine.key,
            quantity: 1
          })
        });

        cart = await getCart();
      }
    }

    await refreshOfferUi(cart);

    return cart;
  }

  function reconcileCart() {
    if (STATE.reconcilePromise) {
      STATE.reconcileQueued = true;
      return STATE.reconcilePromise;
    }

    const currentPromise = runReconcile();

    STATE.reconcilePromise = currentPromise;

    return currentPromise.finally(() => {
      if (STATE.reconcilePromise === currentPromise) {
        STATE.reconcilePromise = null;
      }

      STATE.ignoreObserverUntil =
        Date.now() + CONFIG.observerCooldownMs;

      if (STATE.reconcileQueued) {
        STATE.reconcileQueued = false;
        scheduleReconcile();
      }
    });
  }

  /* ------------------------------------------------ *
   * Enrollment loading state
   *
   * A visual mask over real checkout latency — never a
   * source of it. The cart add and the navigation start
   * immediately; the check/ring/ding run in parallel
   * and live exactly as long as the navigation takes.
   * ------------------------------------------------ */

  function buildLoadingElement() {
    const wrap = document.createElement("div");

    wrap.className = "chapie-vip-loading";
    wrap.setAttribute("role", "status");
    wrap.setAttribute("aria-live", "polite");
    wrap.setAttribute("aria-busy", "true");

    const spinner = document.createElement("div");

    spinner.className = "chapie-vip-loading__spinner";

    spinner.innerHTML =
      '<div class="chapie-vip-loading__ring"></div>' +
      '<svg class="chapie-vip-loading__check" ' +
      'viewBox="0 0 24 24" aria-hidden="true" ' +
      'focusable="false"><path d="M5 12.5l4.4 4.4L19 7.5" ' +
      'fill="none" stroke="currentColor" ' +
      'stroke-width="2.6" stroke-linecap="round" ' +
      'stroke-linejoin="round"/></svg>';

    const text = document.createElement("p");

    text.className = "chapie-vip-loading__text";
    text.textContent = "Applying FREE Shipping with VIP...";

    wrap.append(spinner, text);

    return wrap;
  }

  /*
   * The AudioContext must be constructed and resumed
   * inside the click gesture or strict autoplay policies
   * suppress it. It is therefore created synchronously
   * at press time and only the oscillator is scheduled
   * later, when the checkmark finishes drawing.
   */
  function createAudioContext() {
    try {
      const Ctx =
        window.AudioContext || window.webkitAudioContext;

      if (!Ctx) {
        return null;
      }

      const ctx = new Ctx();

      if (ctx.state === "suspended") {
        ctx.resume().catch(() => {});
      }

      return ctx;
    } catch {
      return null;
    }
  }

  /*
   * Every failure path is swallowed — sound is garnish,
   * never load-bearing.
   */
  function playConfirmationDing(ctx) {
    try {
      if (!ctx) {
        return;
      }

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = "sine";
      osc.frequency.value = 987;

      gain.gain.setValueAtTime(0.0001, ctx.currentTime);

      gain.gain.exponentialRampToValueAtTime(
        0.07,
        ctx.currentTime + 0.02
      );

      gain.gain.exponentialRampToValueAtTime(
        0.0001,
        ctx.currentTime + 0.35
      );

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start();
      osc.stop(ctx.currentTime + 0.4);
    } catch {
      /* silent by design */
    }
  }

  function closeAudioContext(ctx) {
    try {
      ctx?.close?.().catch(() => {});
    } catch {
      /* silent by design */
    }
  }

  function showLoadingState(hostCard) {
    if (!hostCard) {
      return null;
    }

    const loader = buildLoadingElement();

    hostCard.classList.add("chapie-vip-loading-host");
    hostCard.append(loader);

    /*
     * Double rAF so the fade transition actually runs
     * from opacity 0 after insertion.
     */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        loader.classList.add("chapie-vip-loading--visible");
      });
    });

    /*
     * Created now, inside the gesture; played later.
     */
    const audioContext = createAudioContext();

    const dingTimer = window.setTimeout(() => {
      playConfirmationDing(audioContext);
    }, 600);

    return { loader, hostCard, dingTimer, audioContext };
  }

  function hideLoadingState(handle) {
    if (!handle) {
      return;
    }

    window.clearTimeout(handle.dingTimer);

    closeAudioContext(handle.audioContext);

    handle.loader.remove();

    handle.hostCard.classList.remove(
      "chapie-vip-loading-host"
    );
  }

  /*
   * The fast enrollment path shared by the widget CTA
   * and the popup CTA. Exactly two network round-trips
   * before navigation — one cart read (guards against
   * stale state adding a duplicate membership) and the
   * add itself. No post-add cart refresh, no UI
   * re-render, no dispatch: the page is being left.
   * Returns true when the caller should navigate.
   */
  async function performEnrollment(surface) {
    const cart = await getCart();

    if (!hasQualifyingProduct(cart)) {
      throw new Error(
        "No qualifying merchandise in cart."
      );
    }

    /*
     * A membership already present (ours or manual)
     * means nothing to add — proceed straight to
     * checkout.
     */
    if (findVipLine(cart) || hasAnyVipLine(cart)) {
      return true;
    }

    const terms = await getOfferTerms();

    await addVipLine(terms, surface);

    return true;
  }

  /*
   * The widget CTA is an enroll-and-continue action: one
   * affirmative press on a card that displays the full
   * offer terms, then straight to checkout with the
   * membership line in the cart. The loading state
   * appears at press time and the navigation fires the
   * instant the add resolves — the animation masks the
   * real checkout latency and never extends it. On
   * failure the customer stays put with the button
   * re-enabled and the error shown — they are never
   * sent to checkout in an unknown state.
   */
  async function handleCtaClick(event) {
    if (STATE.toggleBusy || isOfferBlocked()) {
      return;
    }

    STATE.toggleBusy = true;

    setControlsDisabled(true);

    const card = event?.currentTarget?.closest?.(
      ".chapie-vip-offer__card"
    );

    const loading = showLoadingState(card);

    try {
      await performEnrollment("cart_widget");

      /*
       * Navigate immediately. The loader stays up (and
       * the ring keeps spinning) until the new document
       * replaces this one — however long that takes,
       * and not a millisecond longer.
       */
      window.location.href = rootUrl("checkout");
    } catch (error) {
      console.error("[Chapie VIP] Add error:", error);

      hideLoadingState(loading);

      setStatus(
        "Unable to add Chapie VIP. Please try again."
      );

      STATE.toggleBusy = false;

      setControlsDisabled(false);

      await refreshOfferUi().catch(() => {});
    }
  }

  /*
   * The check in the added bar takes VIP back out. It
   * does not navigate — the customer is managing their
   * cart, not checking out.
   */
  async function handleRemoveCheckClick() {
    if (STATE.toggleBusy) {
      return;
    }

    STATE.toggleBusy = true;

    setControlsDisabled(true);

    setRemovedByUser();

    try {
      setStatus("Removing Chapie VIP…");

      await removeVip();

      setStatus("");
    } catch (error) {
      console.error("[Chapie VIP] Remove error:", error);

      setStatus(
        "Unable to remove Chapie VIP. Please try again."
      );
    } finally {
      STATE.toggleBusy = false;

      setControlsDisabled(false);

      await refreshOfferUi().catch(() => {});
    }
  }

  /*
   * The check in the removed bar re-enrolls, with the
   * terms displayed beside it. Also stays on the page.
   */
  async function handleReAddCheckClick() {
    if (STATE.toggleBusy || isOfferBlocked()) {
      return;
    }

    STATE.toggleBusy = true;

    setControlsDisabled(true);

    try {
      setStatus("Adding Chapie VIP…");

      await ensureVip({ surface: "cart_widget_readd" });

      setStatus("");
    } catch (error) {
      console.error("[Chapie VIP] Re-add error:", error);

      setStatus(
        "Unable to add Chapie VIP. Please try again."
      );
    } finally {
      STATE.toggleBusy = false;

      setControlsDisabled(false);

      await refreshOfferUi().catch(() => {});
    }
  }

  function bindOfferInstances() {
    getOfferInstances().forEach(container => {
      if (container.dataset.chapieVipBound === "true") {
        return;
      }

      container.dataset.chapieVipBound = "true";

      const elements = getInstanceElements(container);

      elements.cta?.addEventListener(
        "click",
        handleCtaClick
      );

      elements.removeCheck?.addEventListener(
        "click",
        handleRemoveCheckClick
      );

      elements.readdCheck?.addEventListener(
        "click",
        handleReAddCheckClick
      );
    });
  }

  /* ------------------------------------------------ *
   * Checkout interrupt popup
   * ------------------------------------------------ */

  function buildModal(terms) {
    const root = document.createElement("div");

    root.className = "chapie-vip-modal";
    root.setAttribute("data-chapie-vip-modal", "");
    root.hidden = true;

    /*
     * The dialog carries the widget's root class so the
     * card inherits the identical variables, fonts, and
     * component styles. It must NOT carry the
     * data-chapie-vip-offer attribute — the widget
     * visibility machinery would treat it as a cart
     * instance and hide it.
     */
    const dialog = document.createElement("div");

    dialog.className =
      "chapie-vip-modal__dialog chapie-vip-offer";

    dialog.setAttribute("role", "dialog");
    dialog.setAttribute("aria-modal", "true");
    dialog.setAttribute(
      "aria-labelledby",
      "chapie-vip-modal-title"
    );

    const card = document.createElement("div");

    card.className =
      "chapie-vip-offer__card chapie-vip-offer__inactive";

    const content = document.createElement("div");

    content.className = "chapie-vip-modal__content";

    content.append(
      buildOfferCardContent(terms, {
        ctaAttr: "data-chapie-vip-accept",
        declineLink: true
      })
    );

    card.append(content);

    /*
     * Container art for the wide-viewport layout.
     * Decorative only: aria-hidden, empty alt, and
     * display: none below 1100px. Eager-loaded because
     * the images are only built at first open anyway —
     * lazy inside a hidden container defers the fetch
     * and causes visible pop-in.
     */
    const art = document.createElement("div");

    art.className = "chapie-vip-modal__art";
    art.setAttribute("aria-hidden", "true");

    [
      CONFIG.brandAssets.containerWhite,
      CONFIG.brandAssets.containerBlue
    ].forEach((src, index) => {
      const image = document.createElement("img");

      image.className =
        "chapie-vip-modal__art-item " +
        `chapie-vip-modal__art-item--${
          index === 0 ? "left" : "right"
        }`;

      image.src = src;
      image.alt = "";
      image.loading = "eager";

      art.append(image);
    });

    card.append(art);

    const close = document.createElement("button");

    close.type = "button";
    close.className = "chapie-vip-modal__close";
    close.setAttribute("aria-label", COPY.close);
    close.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true" ' +
      'focusable="false"><path d="M6 6l12 12M18 6L6 18" ' +
      'stroke="currentColor" stroke-width="2.2" ' +
      'stroke-linecap="round" fill="none"/></svg>';

    card.prepend(close);

    const status = document.createElement("p");

    status.className = "chapie-vip-modal__status";
    status.setAttribute("data-chapie-vip-modal-status", "");
    status.setAttribute("aria-live", "polite");

    content.append(status);

    dialog.append(card);

    root.append(dialog);

    /*
     * Three exits, three meanings. CTA: consent —
     * enroll and continue. "No thanks": decline —
     * nothing added, continue to checkout. X / Escape:
     * close — stay on the page, no decision. There is
     * no backdrop dismissal: the card is a full-screen
     * takeover, so no backdrop is ever exposed to
     * click.
     *
     * A successful enroll, a decline, and a close each
     * mark the interrupt as seen, so the
     * next checkout press always goes straight through
     * and the offer can never become a gate.
     *
     * Activation is pointer-first, delegated on the
     * modal root, NOT per-button click listeners. The
     * theme's cart drawer swallows the first document-
     * level CLICK outside itself to close (click-outside
     * -to-dismiss), and this popup is "outside" as far
     * as the drawer knows — which forced a double tap on
     * mobile whenever the popup opened over the drawer.
     * pointerup is a different event the drawer never
     * touches, so the first tap lands. A tap/scroll
     * discriminator prevents scroll-release activations,
     * and a single-fire guard stops pointerup + the
     * trailing click from double-running; the click
     * listener remains as the keyboard/AT path.
     */
    root.addEventListener(
      "pointerdown",
      event => {
        STATE.modalPointerDown = {
          x: event.clientX,
          y: event.clientY
        };
      },
      true
    );

    root.addEventListener(
      "pointerup",
      event => {
        const down = STATE.modalPointerDown;

        STATE.modalPointerDown = null;

        if (!down) {
          return;
        }

        const moved = Math.hypot(
          event.clientX - down.x,
          event.clientY - down.y
        );

        if (moved > 12) {
          return;
        }

        dispatchModalAction(event.target);
      },
      true
    );

    root.addEventListener(
      "click",
      event => {
        dispatchModalAction(event.target);
      },
      true
    );

    document.body.append(root);

    return root;
  }

  /*
   * Routes a tap/click/keypress inside the modal to the
   * right handler exactly once. pointerup and the
   * trailing click both arrive here; whichever is first
   * wins and the echo inside the guard window is
   * ignored. The window is short so a failed accept can
   * be retried immediately after its error renders.
   */
  function dispatchModalAction(target) {
    if (!(target instanceof Element)) {
      return;
    }

    const control = target.closest(
      "[data-chapie-vip-accept]," +
        "[data-chapie-vip-decline]," +
        ".chapie-vip-modal__close"
    );

    if (!control) {
      return;
    }

    const now = Date.now();

    if (now - STATE.modalActionAt < 700) {
      return;
    }

    STATE.modalActionAt = now;

    if (control.hasAttribute("data-chapie-vip-accept")) {
      handleModalAccept();
      return;
    }

    if (control.hasAttribute("data-chapie-vip-decline")) {
      handleModalDecline();
      return;
    }

    handleModalClose();
  }

  function setModalStatus(message = "") {
    STATE.modalElement
      ?.querySelector("[data-chapie-vip-modal-status]")
      ?.replaceChildren(document.createTextNode(message));
  }

  function setModalBusy(busy) {
    STATE.modalBusy = busy;

    STATE.modalElement
      ?.querySelectorAll("button")
      .forEach(button => {
        button.disabled = busy;
      });
  }

  function getModalFocusables() {
    if (!STATE.modalElement) {
      return [];
    }

    return Array.from(
      STATE.modalElement.querySelectorAll(
        'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      )
    ).filter(
      node =>
        node.offsetWidth ||
        node.offsetHeight ||
        node.getClientRects().length
    );
  }

  /*
   * Tab is confined to the dialog while it is open, so
   * keyboard users cannot wander into the page behind
   * a modal that declares aria-modal="true".
   */
  function trapModalFocus(event) {
    const focusables = getModalFocusables();

    if (!focusables.length) {
      event.preventDefault();
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || !STATE.modalElement.contains(active))) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function handleModalKeydown(event) {
    if (event.key === "Escape" && !STATE.modalBusy) {
      handleModalClose();
      return;
    }

    if (event.key === "Tab" && STATE.modalOpen) {
      trapModalFocus(event);
    }
  }

  /*
   * Locks page scroll behind the full-screen popup.
   * Without this, touch-scrolling on the overlay moves
   * the page underneath on iOS. The prior scroll offset
   * is restored on close.
   */
  function setBodyScrollLocked(locked, { restoreScroll = true } = {}) {
    const body = document.body;

    if (locked) {
      STATE.scrollY = window.scrollY;

      body.style.top = `-${STATE.scrollY}px`;
      body.classList.add("chapie-vip-scroll-locked");

      return;
    }

    if (!body.classList.contains("chapie-vip-scroll-locked")) {
      return;
    }

    body.classList.remove("chapie-vip-scroll-locked");
    body.style.top = "";

    /*
     * Skipped on bfcache restore: the browser reinstates
     * the scroll position itself, and STATE.scrollY from
     * before the navigation would fight it.
     */
    if (restoreScroll) {
      window.scrollTo(0, STATE.scrollY || 0);
    }
  }

  function openModal(terms) {
    if (!STATE.modalElement) {
      STATE.modalElement = buildModal(terms);
    }

    /*
     * Remember the control that opened the popup so
     * focus returns there when it closes in place.
     */
    STATE.modalOpener =
      STATE.pendingCheckoutTrigger &&
      document.contains(STATE.pendingCheckoutTrigger)
        ? STATE.pendingCheckoutTrigger
        : null;

    window.clearTimeout(STATE.modalHideTimer);

    STATE.modalElement.hidden = false;

    /*
     * Double rAF so the opacity transition runs from 0
     * rather than being collapsed with the unhide.
     */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        STATE.modalElement?.classList.add(
          "chapie-vip-modal--visible"
        );
      });
    });

    STATE.modalOpen = true;

    STATE.modalPointerDown = null;
    STATE.modalActionAt = 0;

    setBodyScrollLocked(true);

    setModalStatus("");
    setModalBusy(false);

    document.addEventListener("keydown", handleModalKeydown);

    STATE.modalElement
      .querySelector("[data-chapie-vip-accept]")
      ?.focus();
  }

  function resumeCheckout() {
    const trigger = STATE.pendingCheckoutTrigger;

    STATE.pendingCheckoutTrigger = null;
    STATE.bypassInterrupt = true;

    if (trigger && document.contains(trigger)) {
      trigger.click();
      return;
    }

    window.location.href = rootUrl("checkout");
  }

  async function handleModalAccept() {
    if (STATE.modalBusy) {
      return;
    }

    setModalBusy(true);

    const card = STATE.modalElement?.querySelector(
      ".chapie-vip-offer__card"
    );

    const loading = showLoadingState(card);

    try {
      await performEnrollment("checkout_modal");

      /*
       * Marked seen only now. A failed attempt must not
       * gate the popup for the rest of the session — the
       * customer neither enrolled nor declined.
       */
      setInterruptSeen();

      /*
       * Resume checkout the moment the add resolves.
       * The popup (and its spinner) stays visible while
       * the checkout document loads, then is replaced
       * by it — the animation covers exactly the real
       * navigation time.
       */
      STATE.modalOpen = false;

      document.removeEventListener(
        "keydown",
        handleModalKeydown
      );

      resumeCheckout();
    } catch (error) {
      console.error("[Chapie VIP] Modal add error:", error);

      hideLoadingState(loading);

      setModalBusy(false);

      setModalStatus(
        "Unable to add Chapie VIP. Continue to checkout " +
          "or try again."
      );
    }
  }

  /*
   * "No thanks, I'll pay for shipping" — the wording
   * promises decline-and-proceed, so it does exactly
   * that: nothing added, marked as seen, straight on to
   * checkout. NOTE: this supersedes the earlier spec
   * that the link mirror the X. The X (and Escape)
   * remain close-in-place for "let me think"; the link
   * is the explicit "no, continue my checkout". To
   * revert to X-equivalent behavior, bind the decline
   * button to handleModalClose instead.
   */
  /*
   * Shared teardown for both stay-and-close and
   * decline-and-continue, so the fade, the scroll
   * unlock, and the listener cleanup can never diverge
   * between the two exits.
   */
  function teardownModalUi({ restoreFocus = false } = {}) {
    STATE.modalOpen = false;

    if (STATE.modalElement) {
      STATE.modalElement.classList.remove(
        "chapie-vip-modal--visible"
      );

      window.clearTimeout(STATE.modalHideTimer);

      STATE.modalHideTimer = window.setTimeout(() => {
        if (!STATE.modalOpen && STATE.modalElement) {
          STATE.modalElement.hidden = true;
        }
      }, 220);
    }

    setBodyScrollLocked(false);

    document.removeEventListener(
      "keydown",
      handleModalKeydown
    );

    if (
      restoreFocus &&
      STATE.modalOpener &&
      document.contains(STATE.modalOpener)
    ) {
      STATE.modalOpener.focus();
    }

    STATE.modalOpener = null;
  }

  function handleModalDecline() {
    if (STATE.modalBusy) {
      return;
    }

    setInterruptSeen();

    teardownModalUi();

    resumeCheckout();
  }

  function handleModalClose() {
    if (STATE.modalBusy) {
      return;
    }

    setInterruptSeen();

    STATE.pendingCheckoutTrigger = null;

    /*
     * Focus returns to the control that opened the
     * popup, so a keyboard user is not dropped at the
     * top of the document.
     */
    teardownModalUi({ restoreFocus: true });

    /*
     * The widget's offer state is gated on the popup
     * having been dismissed, so reveal it right away.
     */
    refreshOfferUi().catch(() => {});
  }

  function findCheckoutTrigger(target) {
    if (!(target instanceof Element)) {
      return null;
    }

    return target.closest(
      'button[name="checkout"],' +
        'input[name="checkout"],' +
        '[data-chapie-vip-checkout],' +
        'a[href$="/checkout"],' +
        'a[href*="/checkout?"]'
    );
  }

  async function handleCheckoutIntercept(event) {
    if (STATE.bypassInterrupt) {
      STATE.bypassInterrupt = false;
      return;
    }

    const trigger = findCheckoutTrigger(event.target);

    if (!trigger) {
      return;
    }

    /*
     * While the popup is open, checkout clicks are
     * suppressed rather than ignored. The popup is
     * pointer-events: none for the two frames before
     * its fade-in class lands, so an ignored click in
     * that window would fall through to the Checkout
     * button underneath and navigate past the offer.
     */
    if (STATE.modalOpen) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    /*
     * Cheap synchronous exits first, so the vast
     * majority of checkout clicks are never delayed.
     */
    if (isOfferBlocked() || interruptSeen()) {
      return;
    }

    /*
     * Every checkout click is suppressed while a first
     * one is still resolving. Two earlier shapes were
     * both wrong: suppressing before the guard killed
     * the second click outright, and returning without
     * suppressing let it navigate straight past the
     * offer. The first click owns the flow and will
     * either open the popup or resume checkout; until
     * then nothing else gets through.
     */
    event.preventDefault();
    event.stopPropagation();

    if (STATE.interceptInFlight) {
      return;
    }

    STATE.interceptInFlight = true;

    STATE.pendingCheckoutTrigger = trigger;

    try {
      const cart = await getCart();

      /*
       * Already consented, or nothing to offer against:
       * proceed untouched. A cart that already contains
       * a membership is never interrupted.
       */
      if (
        hasAnyVipLine(cart) ||
        !hasQualifyingProduct(cart)
      ) {
        resumeCheckout();
        return;
      }

      const terms = await getOfferTerms();

      openModal(terms);
    } catch (error) {
      /*
       * Anything unexpected must not trap the customer
       * on the cart page. Fail open to checkout.
       */
      warn("Checkout interrupt failed open.", error);

      resumeCheckout();
    } finally {
      STATE.interceptInFlight = false;
    }
  }

  document.addEventListener(
    "click",
    handleCheckoutIntercept,
    true
  );

  /* ------------------------------------------------ *
   * Cart change detection
   * ------------------------------------------------ */

  function stopPolling() {
    if (STATE.pollingTimer) {
      window.clearTimeout(STATE.pollingTimer);
    }

    STATE.pollingTimer = null;
    STATE.pollingStartedAt = 0;
  }

  function beginProductPolling() {
    stopPolling();

    if (isOfferBlocked()) {
      scheduleReconcile();
      return;
    }

    STATE.pollingStartedAt = Date.now();

    const poll = async () => {
      try {
        const cart = await getCart();

        if (hasQualifyingProduct(cart)) {
          stopPolling();
          scheduleReconcile();
          return;
        }
      } catch (error) {
        log("Product polling error:", error);
      }

      const elapsed = Date.now() - STATE.pollingStartedAt;

      if (elapsed < CONFIG.pollingDurationMs) {
        STATE.pollingTimer = window.setTimeout(
          poll,
          CONFIG.pollingIntervalMs
        );
      } else {
        stopPolling();
      }
    };

    STATE.pollingTimer = window.setTimeout(
      poll,
      CONFIG.pollingIntervalMs
    );
  }

  function scheduleReconcile({ fromObserver = false } = {}) {
    const now = Date.now();

    if (fromObserver && now < STATE.ignoreObserverUntil) {
      const retryDelay =
        STATE.ignoreObserverUntil - now + 10;

      window.clearTimeout(STATE.observerRetryTimer);

      STATE.observerRetryTimer = window.setTimeout(() => {
        STATE.observerRetryTimer = null;

        scheduleReconcile({ fromObserver: true });
      }, retryDelay);

      return;
    }

    if (STATE.reconcilePromise) {
      STATE.reconcileQueued = true;
      return;
    }

    window.clearTimeout(STATE.reconcileTimer);

    STATE.reconcileTimer = window.setTimeout(async () => {
      STATE.reconcileTimer = null;

      bindOfferInstances();

      try {
        await reconcileCart();
      } catch (error) {
        console.error(
          "[Chapie VIP] Reconcile error:",
          error
        );
      }
    }, CONFIG.reconcileDelayMs);
  }

  document.addEventListener(
    "submit",
    event => {
      const productForm = event.target.closest(
        'form[action*="/cart/add"]'
      );

      if (!productForm) {
        return;
      }

      const variantId = Number(
        new FormData(productForm).get("id")
      );

      if (
        CONFIG.membershipVariantIds.some(
          candidate => Number(candidate) === variantId
        )
      ) {
        return;
      }

      beginProductPolling();
    },
    true
  );

  [
    "cart:updated",
    "cart:change",
    "cart:refresh",
    "ajaxCart:updated",
    "CartDrawer:open"
  ].forEach(eventName => {
    document.addEventListener(eventName, event => {
      if (event.detail?.source === "chapie-vip") {
        return;
      }

      beginProductPolling();
    });
  });

  function mutationIsOnlyInsideVipOffer(mutation) {
    const target =
      mutation.target instanceof Element
        ? mutation.target
        : mutation.target?.parentElement;

    return Boolean(
      target?.closest?.(
        "[data-chapie-vip-offer],[data-chapie-vip-modal]"
      )
    );
  }

  function initializeObservers() {
    STATE.observers.forEach(observer =>
      observer.disconnect()
    );

    STATE.observers = [];

    const containers = [
      document.querySelector("#cart-drawer"),
      document.querySelector(".shopify-section--main-cart")
    ].filter(Boolean);

    if (!containers.length) {
      /*
       * No cart container on this page. Observing
       * document.body instead would turn every unrelated
       * mutation — sliders, review widgets,
       * personalisation apps — into a cart fetch. Watch
       * only for a cart container appearing, then hand
       * back to the real observers and disconnect.
       */
      watchForCartContainer();

      return;
    }

    containers.forEach(container => {
      const isDrawer = container.id === "cart-drawer";

      const observer = new MutationObserver(mutations => {
        const relevantMutations = mutations.filter(
          mutation =>
            !mutationIsOnlyInsideVipOffer(mutation)
        );

        if (!relevantMutations.length) {
          return;
        }

        const drawerStateChanged =
          isDrawer &&
          relevantMutations.some(
            mutation =>
              mutation.type === "attributes" &&
              ["open", "class", "aria-hidden"].includes(
                mutation.attributeName
              )
          );

        if (drawerStateChanged) {
          beginProductPolling();
        }

        scheduleReconcile({ fromObserver: true });
      });

      const observerOptions = {
        childList: true,
        subtree: true
      };

      if (isDrawer) {
        observerOptions.attributes = true;

        observerOptions.attributeFilter = [
          "open",
          "class",
          "aria-hidden"
        ];
      }

      observer.observe(container, observerOptions);

      STATE.observers.push(observer);
    });
  }

  /*
   * Short-lived, self-terminating watcher for a cart
   * container that is injected after load (drawer
   * themes). Disconnects the moment it finds one, and
   * gives up after a bounded window so it can never sit
   * on document.body for the life of the page.
   */
  function watchForCartContainer() {
    if (STATE.containerWatcher) {
      return;
    }

    const findContainer = () =>
      document.querySelector("#cart-drawer") ||
      document.querySelector(".shopify-section--main-cart");

    const stop = () => {
      STATE.containerWatcher?.disconnect();
      STATE.containerWatcher = null;

      window.clearTimeout(STATE.containerWatchTimer);
      STATE.containerWatchTimer = null;
    };

    const observer = new MutationObserver(() => {
      if (!findContainer()) {
        return;
      }

      stop();

      initializeObservers();
      scheduleReconcile();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    STATE.containerWatcher = observer;

    STATE.containerWatchTimer = window.setTimeout(
      stop,
      CONFIG.containerWatchMs
    );
  }

  async function initialize() {
    if (!customerFlagsLoaded()) {
      warn(
        "ChapieVipCustomer flags missing — offer suppressed."
      );
    }

    bindOfferInstances();
    initializeObservers();

    getOfferTerms().catch(error => {
      log("Terms prefetch failed:", error);
    });

    try {
      await reconcileCart();
    } catch (error) {
      console.error(
        "[Chapie VIP] Initialization error:",
        error
      );
    }
  }

  /*
   * Tears down every transient state so a restored page
   * is usable again.
   *
   * Back-navigating from checkout restores this page
   * from the bfcache exactly as it was left: the
   * enrollment loader still overlaying the card, the
   * popup still visible, and toggleBusy / modalBusy
   * still latched — which looked like a permanent
   * "Applying FREE Shipping..." freeze. The VIP line
   * itself is real and stays in the cart; only the UI
   * needs resetting.
   */
  function resetTransientUi() {
    document
      .querySelectorAll(".chapie-vip-loading")
      .forEach(node => node.remove());

    document
      .querySelectorAll(".chapie-vip-loading-host")
      .forEach(node =>
        node.classList.remove("chapie-vip-loading-host")
      );

    if (STATE.modalElement) {
      STATE.modalElement.hidden = true;
    }

    STATE.modalOpen = false;
    STATE.modalBusy = false;
    STATE.modalPointerDown = null;
    STATE.modalActionAt = 0;
    STATE.modalOpener = null;

    window.clearTimeout(STATE.modalHideTimer);

    if (STATE.modalElement) {
      STATE.modalElement.classList.remove(
        "chapie-vip-modal--visible"
      );
    }

    document.removeEventListener(
      "keydown",
      handleModalKeydown
    );

    setBodyScrollLocked(false, { restoreScroll: false });

    STATE.toggleBusy = false;
    STATE.bypassInterrupt = false;
    STATE.interceptInFlight = false;
    STATE.pendingCheckoutTrigger = null;

    setControlsDisabled(false);
    setStatus("");
  }

  function boot() {
    if (document.readyState === "loading") {
      document.addEventListener(
        "DOMContentLoaded",
        initialize,
        { once: true }
      );
    } else {
      initialize();
    }
  }

  document.addEventListener("shopify:section:load", () => {
    bindOfferInstances();
    initializeObservers();
    scheduleReconcile();
  });

  window.addEventListener("pageshow", event => {
    /*
     * Only a bfcache restore needs the teardown. Running
     * it on every show risked clearing busy flags for an
     * enrollment a customer started before the load
     * event fired.
     */
    if (!event.persisted) {
      return;
    }

    resetTransientUi();

    bindOfferInstances();
    initializeObservers();
    scheduleReconcile();
  });

  boot();

  window.ChapieVip = {
    add: ensureVip,
    remove: removeVip,
    reconcile: reconcileCart,
    refreshUi: refreshOfferUi,
    terms: getOfferTerms,
    isActiveMember: isActiveVipMember,
    isTrialUsed: isTrialUsed,
    isBlocked: isOfferBlocked
  };
})();
